import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Hammer, Home as HomeIcon, ShieldCheck, Search, Star, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import FeaturedStrip from "../components/FeaturedStrip";
import { useSEO } from "../lib/useSEO";

const CATS = ["Plumbing","Electrical","Roofing","Landscaping","Renovation","HVAC","Painting","Flooring","Cleaners","Photography","Private Home Chef","Child Care"];

export default function Landing() {
  const navigate = useNavigate();
  useSEO({
    title: "Columbia Valley Contractors Directory",
    description: "Find trusted local contractors in the Columbia Valley — plumbing, electrical, roofing, landscaping, photography and more. From Sparwood to Canal Flats."
  });
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/36447570/pexels-photo-36447570.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt="Columbia Valley home"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1c2a23]/85 via-[#1c2a23]/60 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-10 pb-24 lg:pt-16 lg:pb-36">
          <div className="max-w-2xl text-white">
            <div className="mb-3 text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-none" style={{ fontFamily: "Outfit" }}>Kootenay Home Pros</div>
            <div className="overline text-[#EAB89F] mb-5 inline-flex items-center gap-2">
              <MapPin className="w-3 h-3" /> Kootenay Region · British Columbia
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6" style={{ fontFamily: "Outfit" }}>
              Find trusted home contractors across the Kootenays.
            </h1>
            <p className="text-base sm:text-lg leading-relaxed text-white/85 mb-8 max-w-xl">
              Across the Kootenay region of BC — connect with vetted local pros for renovations, roofing, photography and every job in between.
            </p>
          </div>
        </div>
      </section>

      {/* Stats / value props */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "Vetted Locals", body: "Every pro listed is part of the Columbia Valley community." },
            { icon: Hammer, title: "12 Trades", body: "From plumbing to photography — and everything in between." },
            { icon: HomeIcon, title: "Homeowner First", body: "Post jobs in minutes, hear back from real local pros fast." },
          ].map(({ icon: Ic, title, body }, i) => (
            <div key={i} className="bg-white border border-[#E2DCD0] rounded-xl p-8 hover:-translate-y-1 hover:shadow-md transition-all">
              <div className="w-11 h-11 rounded-lg bg-[#EAE5D9] flex items-center justify-center mb-5">
                <Ic className="w-5 h-5 text-[#314A3E]" />
              </div>
              <h3 className="text-xl font-semibold tracking-tight mb-2" style={{ fontFamily: "Outfit" }}>{title}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured pros */}
      <FeaturedStrip />

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline text-[#B85C47] mb-3">Browse by trade</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>The trades you need</h2>
          </div>
          <Link to="/directory" className="hidden sm:flex items-center text-sm font-semibold text-[#314A3E] hover:gap-2 gap-1 transition-all">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {CATS.map((c, idx) => (
            <Link
              key={c}
              to={`/directory?category=${encodeURIComponent(c)}`}
              data-testid={`category-card-${c.toLowerCase()}`}
              className="group bg-[#EAE5D9] hover:bg-[#314A3E] rounded-xl p-5 fade-up text-center transition-colors"
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <div className="text-[#314A3E] group-hover:text-[#EAB89F] font-semibold text-sm tracking-tight transition-colors">
                {c}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-[#EAE5D9] mt-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline text-[#B85C47] mb-3">Pricing for the valley</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3" style={{ fontFamily: "Outfit" }}>
              Built local. Priced fair.
            </h2>
            <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 bg-white/80 border border-[#B85C47]/40 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#B85C47] animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#B85C47]">Introductory Pricing</span>
            </div>
            <p className="text-stone-700 leading-relaxed mb-6">
              Contractors $7.99/mo · Homeowners $10.99/mo · Or grab a $6 one-time fast pass (1 week). No long-term commitments.
            </p>
            <Button data-testid="cta-pricing-btn" onClick={() => navigate("/pricing")} size="lg" className="bg-[#314A3E] hover:bg-[#243a30] text-white">
              See plans <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="relative h-64 sm:h-80 rounded-2xl overflow-hidden shadow-lg">
            <img src="https://images.pexels.com/photos/34153285/pexels-photo-34153285.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Valley home" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Testimonial-like */}
      <section className="max-w-4xl mx-auto px-5 lg:px-10 py-20 text-center">
        <div className="flex justify-center gap-1 mb-4">
          {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-[#B85C47] text-[#B85C47]" />)}
        </div>
        <p className="text-xl sm:text-2xl leading-snug text-stone-800 mb-5" style={{ fontFamily: "Outfit", letterSpacing: "-0.01em" }}>
          "Found a roofer in Fairmont within a day. The directory is exactly what the valley needed."
        </p>
        <div className="text-sm text-stone-500">— Sarah M., Windermere</div>
      </section>

      {/* Areas We Serve — local SEO */}
      <section className="bg-[#F0EBE1] py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="overline text-[#B85C47] mb-3">Where we work</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-5" style={{ fontFamily: "Outfit" }}>Serving the entire Kootenay region</h2>
          <p className="text-stone-700 leading-relaxed max-w-3xl mb-10">
            Kootenay Home Pros connects local contractors with homeowners across every community in the Kootenay region of British Columbia — from the West Kootenays (Nelson, Trail, Castlegar) through the Slocan Valley and Columbia Valley up to Revelstoke and Golden, and across the East Kootenays from Cranbrook to Fernie. Wherever you live in the Kootenays, you'll find a trusted local pro here.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {["Nelson","Trail","Castlegar","Creston","Cranbrook","Kimberley","Fernie","Invermere","Golden","Revelstoke"].map(t => (
              <div key={t} className="bg-white border border-[#E2DCD0] rounded-lg px-4 py-3 text-sm font-semibold text-[#314A3E]">{t}, BC</div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — featured snippet bait */}
      <section className="max-w-4xl mx-auto px-5 lg:px-10 py-20">
        <div className="overline text-[#B85C47] mb-3">Frequently asked</div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-10" style={{ fontFamily: "Outfit" }}>About Kootenay Home Pros</h2>
        <div className="space-y-6">
          {[
            {q: "How do I find a contractor in the Kootenays?", a: "Sign up at Kootenay Home Pros, choose a plan, then browse the directory by trade — plumbing, electrical, roofing, renovation, photography and nine other categories. Every contractor listed is local to the Kootenay region of British Columbia."},
            {q: "How much does it cost to use Kootenay Home Pros?", a: "Homeowners pay $10.99/month or $6 for a one-week pass. Contractors pay $7.99/month for full directory access. There are no long-term commitments — cancel anytime."},
            {q: "What towns do you serve?", a: "We serve every community across the Kootenay region — Nelson, Trail, Castlegar, Rossland, Creston, Cranbrook, Kimberley, Fernie, Sparwood, Invermere, Radium Hot Springs, Fairmont Hot Springs, Canal Flats, Golden, Revelstoke, Nakusp, Kaslo and surrounding areas."},
            {q: "Are the contractors vetted?", a: "Every contractor is part of the Columbia Valley community and agrees to our liability terms, including carrying appropriate WorkSafeBC coverage and general liability insurance. Reviews and ratings help homeowners pick the right pro."},
            {q: "Can I post my own home project?", a: "Yes — homeowners with an active subscription can post unlimited projects. Local contractors will submit bids and message you directly through the platform."},
          ].map((item, i) => (
            <details key={i} className="bg-white border border-[#E2DCD0] rounded-xl p-5 group">
              <summary className="font-semibold tracking-tight cursor-pointer list-none flex items-center justify-between" style={{ fontFamily: "Outfit" }}>
                {item.q}
                <span className="text-[#B85C47] text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-sm text-stone-700 leading-relaxed mt-3">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* JSON-LD FAQPage for rich snippets */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {"@type":"Question","name":"How do I find a contractor in the Kootenays?","acceptedAnswer":{"@type":"Answer","text":"Sign up at Kootenay Home Pros, choose a plan, then browse the directory by trade — plumbing, electrical, roofing, renovation, photography and nine other categories. Every contractor listed is local to the Kootenay region of British Columbia."}},
          {"@type":"Question","name":"How much does it cost to use Kootenay Home Pros?","acceptedAnswer":{"@type":"Answer","text":"Homeowners pay $10.99/month or $6 for a one-week pass. Contractors pay $7.99/month for full directory access. There are no long-term commitments — cancel anytime."}},
          {"@type":"Question","name":"What towns do you serve?","acceptedAnswer":{"@type":"Answer","text":"We serve every community across the Kootenay region — Nelson, Trail, Castlegar, Rossland, Creston, Cranbrook, Kimberley, Fernie, Sparwood, Invermere, Radium Hot Springs, Fairmont Hot Springs, Canal Flats, Golden, Revelstoke, Nakusp, Kaslo and surrounding areas."}},
          {"@type":"Question","name":"Are the contractors vetted?","acceptedAnswer":{"@type":"Answer","text":"Every contractor is part of the Columbia Valley community and agrees to our liability terms, including carrying appropriate WorkSafeBC coverage and general liability insurance."}},
          {"@type":"Question","name":"Can I post my own home project?","acceptedAnswer":{"@type":"Answer","text":"Yes — homeowners with an active subscription can post unlimited projects. Local contractors will submit bids and message you directly through the platform."}}
        ]
      })}} />
    </div>
  );
}
